import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useTask } from "@/hooks/use-tasks";
import { DocumentDrawer } from "@/v2/documents/DocumentDrawer";

// A patent number as a question writes it: "US9862057B2", or "WO" with a year and a number,
// with or without the slash between them ("WO2019/123456", "WO2019123456").
const NUMBER = /\b(US\d{7,}[AB]\d|WO\d{4}\/?\d+)\b/;

/** A number's letters and digits alone, so "US 9,862,057 B2" and "US9862057B2" read as one. */
function bare(number: string): string {
  return number.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/** Whether a stored publication number is this one: the same, or the same with a kind code after it. */
function names(stored: string, number: string): boolean {
  const held = bare(stored);
  const asked = bare(number);
  return (
    held === asked ||
    (held.startsWith(asked) && /^[A-Z]/.test(held.slice(asked.length)))
  );
}

/** A number the matter holds a document for opens it in the drawer; any other stays text. */
function PatentNumber({ taskId, number }: { taskId: string; number: string }) {
  const { data: task } = useTask(taskId);
  const [open, setOpen] = useState(false);
  const file = (task?.files ?? []).find(
    (held) =>
      held.publication_number !== null &&
      names(held.publication_number, number),
  );
  if (file === undefined) return number;
  return (
    <>
      <Button
        variant="link"
        size="dense"
        title={`Open ${number}`}
        className="h-auto p-0 align-baseline text-[length:inherit] font-[inherit] underline underline-offset-2"
        onClick={() => {
          setOpen(true);
        }}
      >
        {number}
      </Button>
      <DocumentDrawer
        taskId={taskId}
        documents={open ? [{ file, marked: null }] : null}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}

/**
 * A question's words, each patent number in them a way to the matter's
 * document with that number, as v0.29 links them. The document is read only
 * where the words hold a number.
 */
export function QuestionWords({
  taskId,
  text,
}: {
  taskId: string;
  text: string;
}) {
  // Each number keyed by where it starts in the words, which no reordering moves.
  let from = 0;
  return text.split(NUMBER).map((part, at) => {
    const starts = from;
    from += part.length;
    return at % 2 === 1 ? (
      <PatentNumber key={starts} taskId={taskId} number={part} />
    ) : (
      part
    );
  });
}
